import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { RadioGroup, RadioGroupCard } from "@/components/ui/v2/radio-group";
import { Switch } from "@/components/ui/v2/switch";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { PaymentTypeEnum } from "@/types/client/ClientType";
import { AddClientRequest } from "@/types/request/AddClientRequest";

const ClientSettingsStep = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<AddClientRequest>();
  const { canEdit } = usePermissionMatrix();
  const isAdmin = canEdit("clients");

  const paymentOptions = [
    { label: t("common.subscription"), value: PaymentTypeEnum.SUBSCRIPTION },
    { label: t("common.postpaid"), value: PaymentTypeEnum.POST_PAID },
  ];

  return (
    <>
      <FormField
        control={control}
        name="paymentType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientSettings.paymentMethod")}
            </FormLabel>
            <FormControl>
              <RadioGroup value={field.value} onValueChange={field.onChange}>
                {paymentOptions.map((option) => (
                  <RadioGroupCard
                    key={option.value}
                    id={`paymentType-${option.value}`}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="imagePath"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientSettings.logoUrl")}
              <span className="text-muted-foreground font-normal">
                {t("common.optional")}
              </span>
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="https://www.domain.nl/logo.png" />
            </FormControl>
            <FormDescription>
              {t("admin.client.addClient.clientSettings.logoNotice")}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {isAdmin && (
        <FormField
          control={control}
          name="isSelfService"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {t("admin.client.selfService.label", "Self-service client")}
                </FormLabel>
                <FormDescription>
                  {t(
                    "admin.client.selfService.description",
                    "Enable self-service capabilities for this client"
                  )}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </>
  );
};

export default ClientSettingsStep;
